import { useState, useRef } from 'react';
import api from '../api/client';

export function useShopifyUpload(loadProducts) {
  const [showShopifyUploadPopup, setShowShopifyUploadPopup] = useState(false);
  const [uploadShopifyWeek, setUploadShopifyWeek]           = useState('');
  const [uploadShopifyMonth, setUploadShopifyMonth]         = useState('');
  const [uploadShopifyYear, setUploadShopifyYear]           = useState('');
  const [howManyWeeks, setHowManyWeeks]                     = useState(1);
  const [uploadMode, setUploadMode]                         = useState('single');
  const [shopifyPeriodMode, setShopifyPeriodMode]           = useState('weekly');
  const [parsedShopifyData, setParsedShopifyData]           = useState([]);
  const [uploadShopifyStatus, setUploadShopifyStatus]       = useState(null);
  const [isLoadingPreview, setIsLoadingPreview]             = useState(false);
  const [isLoadingUpload, setIsLoadingUpload]               = useState(false);

  // useRef чтобы async функции всегда читали актуальный режим (без stale closure)
  const periodModeRef = useRef('weekly');
  const wrappedSetPeriodMode = (mode) => {
    periodModeRef.current = mode;
    setShopifyPeriodMode(mode);
  };

  const loadShopifyPreview = async () => {
    const isWeekly = periodModeRef.current === 'weekly';

    if (!uploadShopifyYear) {
      setUploadShopifyStatus({ type: 'error', message: 'Please enter year' });
      return;
    }
    if (isWeekly && (!uploadShopifyWeek || howManyWeeks < 1)) {
      setUploadShopifyStatus({ type: 'error', message: 'Please enter week and a valid number of weeks' });
      return;
    }
    if (!isWeekly && !uploadShopifyMonth) {
      setUploadShopifyStatus({ type: 'error', message: 'Please select a month' });
      return;
    }

    setIsLoadingPreview(true);
    setUploadShopifyStatus({ type: 'loading', message: 'Fetching data from Shopify API...' });

    try {
      if (isWeekly) {
        const params = new URLSearchParams({
          startYear:    uploadShopifyYear,
          startWeek:    uploadShopifyWeek,
          howManyWeeks: String(howManyWeeks)
        });
        const { data: result } = await api.get(`/api/shopify-sales-preview?${params}`);

        const flatData = [];
        if (result.data) {
          Object.keys(result.data).forEach(sku => {
            Object.keys(result.data[sku]).forEach(weekKey => {
              const [year, week] = weekKey.split('-W');
              flatData.push({
                sku,
                weeknumber: parseInt(week, 10),
                year:       parseInt(year, 10),
                quantity:   result.data[sku][weekKey]
              });
            });
          });
        }
        setParsedShopifyData(flatData);
        setUploadShopifyStatus({ type: 'success', message: `Found ${flatData.length} sales records from Shopify` });

      } else {
        const params = new URLSearchParams({
          year:  uploadShopifyYear,
          month: uploadShopifyMonth
        });
        const { data: result } = await api.get(`/api/shopify-month-sales-preview?${params}`);

        const flatData = Object.entries(result.data || {}).map(([sku, quantity]) => ({
          sku,
          month:    parseInt(uploadShopifyMonth, 10),
          year:     parseInt(uploadShopifyYear, 10),
          quantity
        }));
        setParsedShopifyData(flatData);
        setUploadShopifyStatus({ type: 'success', message: `Found ${flatData.length} sales records from Shopify` });
      }

    } catch (error) {
      if (error.response) {
        const result = error.response.data;
        setUploadShopifyStatus({ type: 'error', message: result?.error || 'Error fetching data from Shopify', details: result?.details });
      } else {
        setUploadShopifyStatus({ type: 'error', message: 'Error connecting to Shopify API', details: error.message });
      }
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleShopifyUploadSubmit = async () => {
    const isWeekly = periodModeRef.current === 'weekly';

    if (!uploadShopifyYear) {
      setUploadShopifyStatus({ type: 'error', message: 'Please enter year' });
      return;
    }
    if (isWeekly && (!uploadShopifyWeek || howManyWeeks < 1)) {
      setUploadShopifyStatus({ type: 'error', message: 'Please enter week and a valid number of weeks' });
      return;
    }
    if (!isWeekly && !uploadShopifyMonth) {
      setUploadShopifyStatus({ type: 'error', message: 'Please select a month' });
      return;
    }

    setIsLoadingUpload(true);
    setUploadShopifyStatus({ type: 'loading', message: 'Uploading Shopify data to database...' });

    try {
      let result;

      if (isWeekly) {
        ({ data: result } = await api.post('/api/shopify-sales-upload', {
          startYear:    parseInt(uploadShopifyYear, 10),
          startWeek:    parseInt(uploadShopifyWeek, 10),
          howManyWeeks: parseInt(howManyWeeks, 10),
          mode:         uploadMode
        }));
      } else {
        ({ data: result } = await api.post('/api/shopify-month-sales-upload', {
          year:  parseInt(uploadShopifyYear, 10),
          month: parseInt(uploadShopifyMonth, 10)
        }));
      }

      setUploadShopifyStatus({ type: 'success', message: result.message || 'Shopify data uploaded successfully!' });
      setTimeout(() => {
        if (loadProducts) loadProducts();
        closeShopifyUploadPopup();
      }, 2000);

    } catch (error) {
      if (error.response) {
        const result = error.response.data;
        setUploadShopifyStatus({ type: 'error', message: result?.error || 'Error uploading Shopify data', details: result?.details });
      } else {
        setUploadShopifyStatus({ type: 'error', message: 'Error uploading data', details: error.message });
      }
    } finally {
      setIsLoadingUpload(false);
    }
  };

  const closeShopifyUploadPopup = () => {
    setShowShopifyUploadPopup(false);
    setUploadShopifyWeek('');
    setUploadShopifyMonth('');
    setUploadShopifyYear('');
    setHowManyWeeks(1);
    setUploadMode('single');
    wrappedSetPeriodMode('weekly');
    setParsedShopifyData([]);
    setUploadShopifyStatus(null);
  };

  return {
    showShopifyUploadPopup, setShowShopifyUploadPopup,
    uploadShopifyWeek,      setUploadShopifyWeek,
    uploadShopifyMonth,     setUploadShopifyMonth,
    uploadShopifyYear,      setUploadShopifyYear,
    howManyWeeks,           setHowManyWeeks,
    uploadMode,             setUploadMode,
    shopifyPeriodMode,      setShopifyPeriodMode: wrappedSetPeriodMode,
    parsedShopifyData,      setParsedShopifyData,
    uploadShopifyStatus,    setUploadShopifyStatus,
    isLoadingPreview,
    isLoadingUpload,
    loadShopifyPreview,
    handleShopifyUploadSubmit,
    closeShopifyUploadPopup
  };
}